const API_URL = window.location.origin;

async function fetchJson(endpoint) {
  const res = await fetch(`${API_URL}${endpoint}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export async function fetchPrograms() {
  return fetchJson('/api/programs');
}

export async function fetchProgram(programId) {
  return fetchJson(`/api/programs/${programId}`);
}

export async function fetchCourses(programId) {
  const query = programId ? `?programId=${programId}` : '';
  return fetchJson(`/api/courses${query}`);
}

export async function fetchCourse(courseId) {
  return fetchJson(`/api/courses/${courseId}`);
}

export async function fetchQuiz(courseId) {
  return fetchJson(`/api/quizzes/${courseId}`);
}

export async function fetchTrainers() {
  return fetchJson('/api/trainers');
}

export async function fetchTestimonials() {
  return fetchJson('/api/testimonials');
}

export async function fetchStats() {
  return fetchJson('/api/stats');
}

export async function fetchReferentielUrls() {
  return fetchJson('/api/referentiel-urls');
}

export async function fetchSlots() {
  return fetchJson('/api/slots');
}

export async function trackTime(courseId, seconds, userId) {
  return fetch(`${API_URL}/api/progress/track-time`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ courseId, seconds, userId: userId || undefined })
  });
}

export async function fetchUserProgress(userId) {
  return fetchJson(`/api/progress/${userId}`);
}

export async function fetchRecevabilite(userId) {
  return fetchJson(`/api/recevabilite/${userId}`);
}

export async function fetchLivret2Corrections(userId) {
  return fetchJson(`/api/livret2/${userId}`);
}

export async function uploadRecevabiliteFile(userId, programId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/recevabilite/${userId}/upload?programId=${programId}`, {
    method: 'POST',
    body: formData
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de l\'upload');
  }
  return res.json();
}

export function getFileUrl(storagePath) {
  return `${API_URL}/api/files/${storagePath}`;
}

export async function createBooking(data) {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api/bookings`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la réservation');
  }
  return res.json();
}

export async function fetchBookings(userId) {
  return fetchJson(`/api/bookings/${userId}`);
}

export async function cancelBooking(bookingId) {
  const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Erreur lors de l\'annulation');
  return res.json();
}

export async function rescheduleBooking(bookingId, date, time) {
  const res = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date, time })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la modification');
  }
  return res.json();
}

// ============ ADMIN API ============

function adminHeaders(token) {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
}

export async function adminLogin(email, password) {
  const res = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) throw new Error('Identifiants invalides');
  return res.json();
}

export async function adminGetBookings(token) {
  const res = await fetch(`${API_URL}/api/admin/bookings`, { headers: adminHeaders(token) });
  if (!res.ok) throw new Error('Accès refusé');
  return res.json();
}

export async function adminGetCandidates(token) {
  const res = await fetch(`${API_URL}/api/admin/candidates`, { headers: adminHeaders(token) });
  if (!res.ok) throw new Error('Acc\u00e8s refus\u00e9');
  return res.json();
}

export async function adminCreateCandidate(token, data) {
  const res = await fetch(`${API_URL}/api/admin/candidates/create`, {
    method: 'POST',
    headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la cr\u00e9ation');
  }
  return res.json();
}

export async function adminSyncCandidatesFromBookings(token) {
  const res = await fetch(`${API_URL}/api/admin/candidates/sync-from-bookings`, {
    method: 'POST',
    headers: adminHeaders(token)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur lors de la synchronisation');
  }
  return res.json();
}


export async function adminUpdateCandidateNotes(token, userId, notes) {
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}/notes`, {
    method: 'PUT',
    headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes })
  });
  if (!res.ok) throw new Error('Erreur sauvegarde notes');
  return res.json();
}

export async function adminUpdateBookingNotes(token, bookingId, notes) {
  const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}/notes`, {
    method: 'PUT',
    headers: { ...adminHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes })
  });
  if (!res.ok) throw new Error('Erreur sauvegarde notes');
  return res.json();
}

export async function adminGetSlots(token) {
  const res = await fetch(`${API_URL}/api/admin/slots`, { headers: adminHeaders(token) });
  if (!res.ok) throw new Error('Accès refusé');
  return res.json();
}

export async function adminUpdateSlot(token, date, time, blocked, label) {
  const body = { date, time, blocked };
  if (label !== undefined) body.label = label;
  const res = await fetch(`${API_URL}/api/admin/slots`, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Erreur de mise à jour');
  return res.json();
}

export async function adminBulkUpdateSlots(token, slots) {
  const res = await fetch(`${API_URL}/api/admin/slots/bulk`, {
    method: 'POST',
    headers: adminHeaders(token),
    body: JSON.stringify({ slots })
  });
  if (!res.ok) throw new Error('Erreur de mise à jour');
  return res.json();
}


// ============ PAYMENT / CHECKOUT ============

export async function createCheckout(data) {
  const res = await fetch(`${API_URL}/api/payments/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Erreur lors de la création du paiement');
  return res.json();
}

export async function getPaymentStatus(sessionId) {
  return fetchJson(`/api/payments/status/${sessionId}`);
}

export async function getUserPayments(email) {
  const res = await fetch(`${API_URL}/api/payments/user/${encodeURIComponent(email)}`);
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

export async function getUserPurchasedPrograms(email) {
  const res = await fetch(`${API_URL}/api/payments/user/${encodeURIComponent(email)}/programs`);
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

export async function getAccompaniment(userId, programId) {
  const res = await fetch(`${API_URL}/api/accompaniment/${encodeURIComponent(userId)}/${encodeURIComponent(programId)}`);
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

// Activity Progress (slides & quiz) - Backend-persisted
export async function getActivityProgressAPI(userId) {
  return fetchJson(`/api/activity-progress/${encodeURIComponent(userId)}`);
}

export async function updateSlideProgress(userId, activityId, slideIndex, totalSlides) {
  const res = await fetch(`${API_URL}/api/activity-progress/${encodeURIComponent(userId)}/slide`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activityId, slideIndex, totalSlides })
  });
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

export async function updateQuizProgress(userId, activityId, score, totalQuestions) {
  const res = await fetch(`${API_URL}/api/activity-progress/${encodeURIComponent(userId)}/quiz`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activityId, score, totalQuestions })
  });
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}

export async function updateAccompaniment(userId, programId, data) {
  const res = await fetch(`${API_URL}/api/accompaniment/${encodeURIComponent(userId)}/${encodeURIComponent(programId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Erreur');
  return res.json();
}



export async function deletePendingPayment(sessionId) {
  const res = await fetch(`${API_URL}/api/payments/${sessionId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Erreur suppression paiement');
  return res.json();
}

export async function deleteCandidate(userId, token) {
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Erreur suppression candidat');
  return res.json();
}

export async function adminResetPassword(userId, newPassword, token) {
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}/reset-password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ password: newPassword })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur réinitialisation mot de passe');
  }
  return res.json();
}

export async function adminGrantAccess(userId, programId, formula, token, kitId, kitTitle) {
  const body = { programId, formula };
  if (kitId) { body.kitId = kitId; body.kitTitle = kitTitle || kitId; }
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}/grant-access`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Erreur attribution accès');
  }
  return res.json();
}


export async function adminGetCandidatePayments(userId, token) {
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}/payments`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Erreur chargement paiements');
  return res.json();
}

export async function adminCreatePayment(userId, data, token) {
  const res = await fetch(`${API_URL}/api/admin/candidates/${userId}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Erreur création paiement');
  return res.json();
}

export async function adminUpdatePayment(sessionId, data, token) {
  const res = await fetch(`${API_URL}/api/admin/payments/${sessionId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Erreur mise à jour paiement');
  return res.json();
}

export async function adminDeletePayment(sessionId, token) {
  const res = await fetch(`${API_URL}/api/admin/payments/${sessionId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Erreur suppression paiement');
  return res.json();
}

export async function adminSendReminder(sessionId, installmentIndex, token) {
  const res = await fetch(`${API_URL}/api/admin/payments/${sessionId}/reminder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ installmentIndex })
  });
  if (!res.ok) throw new Error('Erreur envoi rappel');
  return res.json();
}

export async function adminSendInvoice(sessionId, token) {
  const res = await fetch(`${API_URL}/api/admin/payments/${sessionId}/invoice`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Erreur envoi facture');
  return res.json();
}

